import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, apiError } from "@/context/AuthContext";
import { useDocumentMeta } from "@/hooks/use-document-meta";
import { Hexagon } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://static.prod-images.emergentagent.com/jobs/abc48b77-7c54-4f71-8dd5-4220d773b6d6/images/c0511f2eefc6bf76bcf60bb82efddc6b3a3a977fcb23ae6d84212be21a7e66a3.png";

export default function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, login, register } = useAuth();
  const [mode, setMode] = useState(location.pathname === "/register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setMode(location.pathname === "/register" ? "register" : "login"); }, [location.pathname]);
  useDocumentMeta(
    mode === "register" ? "Sign Up | Infinite Sequence" : "Log In | Infinite Sequence",
    mode === "register"
      ? "Create your free Infinite Sequence account and start mapping your Brazilian Jiu-Jitsu game with a visual technique library and flow builder. 7-day free trial."
      : "Log in to Infinite Sequence to access your BJJ technique library and drag-and-drop sequence builder."
  );
  useEffect(() => { if (!loading && user) navigate("/dashboard", { replace: true }); }, [user, loading, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "register") await register(form.name, form.email, form.password);
      else await login(form.email, form.password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Visual side */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="BJJ" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/70" />
        <div className="absolute bottom-0 p-12">
          <h2 className="font-heading font-black text-4xl tracking-tighter leading-tight max-w-md">
            Every position has<br />an answer.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-sm">Document it. Connect it. Drill it.</p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fadeup">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <Hexagon className="h-7 w-7 text-primary fill-primary/20" strokeWidth={2.5} />
            <span className="font-heading font-extrabold text-xl tracking-tight">INFINITE SEQUENCE</span>
          </Link>

          <h1 className="font-heading font-bold text-3xl tracking-tight">
            {mode === "register" ? "Create your account" : "Welcome back"}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {mode === "register" ? "Start mapping your game today." : "Log in to continue building."}
          </p>

          <Button
            data-testid="google-auth-btn"
            onClick={handleGoogle}
            variant="outline"
            className="w-full mt-8 h-11 rounded border-border bg-card hover:bg-secondary text-white gap-3"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.06H2.18a11 11 0 000 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.99 10.99 0 002.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input id="name" data-testid="auth-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-11 bg-card border-border" placeholder="Saulo Ribeiro" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input id="email" data-testid="auth-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11 bg-card border-border" placeholder="you@gym.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input id="password" data-testid="auth-password-input" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 h-11 bg-card border-border" placeholder="••••••••" />
            </div>
            <Button data-testid="auth-submit-btn" type="submit" disabled={submitting}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-white rounded font-semibold">
              {submitting ? "Please wait…" : mode === "register" ? "Create account" : "Log in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {mode === "register" ? "Already have an account? " : "New to Infinite Sequence? "}
            <Link data-testid="auth-toggle-link" to={mode === "register" ? "/login" : "/register"}
              className="text-primary hover:underline font-medium inline-block py-2 px-1 -my-2">
              {mode === "register" ? "Log in" : "Create one"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
